/**
 * Scheduling API client
 * Web version - Typed fetch wrappers for location-level scheduling management
 */
import { apiClient } from '../../../services/apiClient';
import type {
  ScheduleDto,
  ScheduleItem,
  SchedulingRule,
  AddScheduleItemRequest,
  CreateSchedulingRuleRequest,
  UpdateSchedulingRuleRequest,
  ReorderItemsRequest,
} from '../types/schedule';

// ---------------------------------------------------------------------------
// Schedule CRUD
// ---------------------------------------------------------------------------

/** Get the complete schedule for a location */
export const getSchedule = (locationId: string) =>
  apiClient.get<ScheduleDto>(`/api/v1/locations/${locationId}/schedule`);

/** Add a playlist to the schedule */
export const addPlaylist = (locationId: string, playlistId: string) =>
  apiClient.post<ScheduleItem>(
    `/api/v1/locations/${locationId}/schedule/items`,
    { type: 'playlist', playlistId } satisfies AddScheduleItemRequest,
  );

/** Add a clip to the schedule */
export const addClip = (locationId: string, contentId: string) =>
  apiClient.post<ScheduleItem>(
    `/api/v1/locations/${locationId}/schedule/items`,
    { type: 'clip', contentId } satisfies AddScheduleItemRequest,
  );

/** Remove an item from the schedule */
export const removeItem = (locationId: string, itemId: string) =>
  apiClient.delete<void>(`/api/v1/locations/${locationId}/schedule/items/${itemId}`);

/** Reorder items in the schedule */
export const reorderItems = (locationId: string, itemIds: string[]) =>
  apiClient.put<void>(
    `/api/v1/locations/${locationId}/schedule/items/reorder`,
    { itemIds } satisfies ReorderItemsRequest,
  );

// ---------------------------------------------------------------------------
// Scheduling Rule CRUD
// ---------------------------------------------------------------------------

/** Create a new scheduling rule for an item */
export const createRule = (
  locationId: string,
  itemId: string,
  request: CreateSchedulingRuleRequest,
) =>
  apiClient.post<SchedulingRule>(
    `/api/v1/locations/${locationId}/schedule/items/${itemId}/rules`,
    request,
  );

/** Update an existing scheduling rule */
export const updateRule = (
  locationId: string,
  itemId: string,
  ruleId: string,
  request: UpdateSchedulingRuleRequest,
) =>
  apiClient.put<SchedulingRule>(
    `/api/v1/locations/${locationId}/schedule/items/${itemId}/rules/${ruleId}`,
    request,
  );

/** Delete a scheduling rule */
export const deleteRule = (locationId: string, itemId: string, ruleId: string) =>
  apiClient.delete<void>(
    `/api/v1/locations/${locationId}/schedule/items/${itemId}/rules/${ruleId}`,
  );
